import type { ReactNode } from "react";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, Chip, Link, Typography } from "@mui/material";
import { format } from "date-fns";
import { baseTheme } from "../../../themes/shipinsure";
import { RESOLUTION_TYPE } from "../constants";
import { formatMoney } from "../formatters";
import { getTrackingUrl } from "../getTrackingUrl";
import {
  statusColors,
  statusPanelSx,
  statusSectionTitleSx,
} from "../statusStyles";
import type { ClaimTranslator, ResolutionSummary } from "../types";

type ResolutionPanelProps = {
  resolution: ResolutionSummary;
  t: ClaimTranslator;
};

export const ResolutionPanel = ({ resolution, t }: ResolutionPanelProps) => {
  if (resolution.type === RESOLUTION_TYPE.REFUND) {
    return (
      <PanelShell title={t("status.resolutionPanel.refundAmount")}>
        <Typography variant="h4" fontWeight={700} mb="8px">
          {formatMoney(resolution.amount, resolution.currency)}
        </Typography>
        <Chip
          label={t("status.resolutionPanel.refundProcessing")}
          sx={{
            mb: "16px",
            backgroundColor: statusColors.refundChipBg,
            color: statusColors.refundChipText,
          }}
        />
        {resolution.issuedAt ? (
          <DetailRow
            label={t("status.resolutionPanel.refundIssuedOn")}
            value={format(new Date(resolution.issuedAt), "MMMM dd, yyyy")}
          />
        ) : null}
        <Box
          mt="16px"
          sx={{
            backgroundColor: baseTheme.colors.secondary,
            borderRadius: "12px",
            padding: "12px 16px",
          }}
        >
          <Typography fontSize="14px">
            {t("status.resolutionPanel.refundTiming")}
          </Typography>
        </Box>
      </PanelShell>
    );
  }

  if (resolution.type === RESOLUTION_TYPE.STORE_CREDIT) {
    return (
      <PanelShell title={t("status.resolutionPanel.storeCreditAmount")}>
        <Typography variant="h4" fontWeight={700} mb="8px">
          {formatMoney(resolution.amount, resolution.currency)}
        </Typography>
        {resolution.discountCode ? (
          <DetailRow
            label={t("status.resolutionPanel.storeCreditCode")}
            value={resolution.discountCode}
          />
        ) : null}
        {resolution.issuedAt ? (
          <DetailRow
            label={t("status.resolutionPanel.refundIssuedOn")}
            value={format(new Date(resolution.issuedAt), "MMMM dd, yyyy")}
          />
        ) : null}
        <Box
          mt="16px"
          sx={{
            backgroundColor: statusColors.storeCreditBg,
            borderRadius: "12px",
            padding: "12px 16px",
          }}
        >
          <Typography fontSize="14px">
            {t("status.resolutionPanel.storeCreditHowTo")}
          </Typography>
        </Box>
      </PanelShell>
    );
  }

  if (resolution.type === RESOLUTION_TYPE.RESHIP) {
    const shipment = resolution.shipment;
    const trackingUrl =
      getTrackingUrl(resolution.reshipShipInsureId) ||
      shipment?.tracking_url ||
      null;

    return (
      <PanelShell title={t("status.resolutionPanel.reshipStatus")}>
        {!shipment?.tracking_number ? (
          <>
            <Chip
              label={t("status.resolutionPanel.reshipPending")}
              sx={{
                mb: "16px",
                backgroundColor: statusColors.reshipChipBg,
                color: statusColors.reshipChipText,
              }}
            />
            <Typography fontSize="14px" color={statusColors.metaText}>
              {t("status.resolutionPanel.reshipNotAvailable")}
            </Typography>
            {trackingUrl ? <TrackingLink href={trackingUrl} t={t} /> : null}
          </>
        ) : (
          <>
            <DetailRow
              label={t("status.resolutionPanel.reshipTracking")}
              value={shipment.tracking_number}
              href={trackingUrl ?? undefined}
            />
            {shipment.carrier ? (
              <DetailRow
                label={t("status.resolutionPanel.reshipCarrier")}
                value={shipment.carrier}
              />
            ) : null}
            {shipment.estimated_delivery_date ? (
              <DetailRow
                label={t("status.resolutionPanel.reshipEta")}
                value={format(
                  new Date(shipment.estimated_delivery_date),
                  "MMMM dd, yyyy",
                )}
              />
            ) : null}
            {trackingUrl ? <TrackingLink href={trackingUrl} t={t} /> : null}
          </>
        )}
      </PanelShell>
    );
  }

  if (resolution.phase !== "resolution") {
    return null;
  }

  return (
    <PanelShell title={t("status.resolutionPanel.pendingTitle")} emphasized>
      <Typography fontSize="14px" color={statusColors.bodyText}>
        {t("status.resolutionPanel.pendingDescription", {
          resolution: resolution.preferredResolution || "resolution",
        })}
      </Typography>
    </PanelShell>
  );
};

const TrackingLink = ({ href, t }: { href: string; t: ClaimTranslator }) => (
  <Link
    href={href}
    target="_blank"
    rel="noreferrer"
    underline="hover"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      mt: "8px",
      fontSize: "14px",
      fontWeight: 600,
      color: baseTheme.colors.primary,
    }}
  >
    {t("status.viewTracking")}
    <OpenInNewIcon sx={{ fontSize: 14 }} />
  </Link>
);

const PanelShell = ({
  title,
  children,
  emphasized = false,
}: {
  title: string;
  children: ReactNode;
  emphasized?: boolean;
}) => (
  <Box mb="16px" sx={statusPanelSx(emphasized)}>
    <Typography sx={{ ...statusSectionTitleSx, mb: "8px" }}>{title}</Typography>
    {children}
  </Box>
);

const DetailRow = ({
  label,
  value,
  href,
}: {
  label: string;
  value: string;
  href?: string;
}) => (
  <Box mb="8px">
    <Typography fontSize="14px" fontWeight={700} color={statusColors.metaText}>
      {label}
    </Typography>
    {href ? (
      <Link
        href={href}
        target="_blank"
        rel="noreferrer"
        underline="hover"
        sx={{
          fontSize: "15px",
          fontWeight: 600,
          color: baseTheme.colors.primary,
          wordBreak: "break-all",
        }}
      >
        {value}
      </Link>
    ) : (
      <Typography fontSize="15px">{value}</Typography>
    )}
  </Box>
);
